import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Switch,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Divider,
} from '@mui/material';
import { ArrowForward, ArrowForwardIos, ExpandMore } from '@mui/icons-material';
import { FormField } from '@shared/components/FormField/FormField';
import type { Role, ModulePermission, CreateRoleRequest, UpdateRoleRequest, Permission, ModuleName } from '../types';
import { AVAILABLE_MODULES, getModulePermissions } from '../types';
import StatusChip from '@shared/components/StatusChip/StatusChip';

interface EditRoleDialogProps {
  open: boolean;
  role: Role | null;
  onClose: () => void;
  onSave: (data: CreateRoleRequest | UpdateRoleRequest) => Promise<void>;
  isSubmitting: boolean;
  mode: 'create' | 'edit';
}

export const EditRoleDialog: React.FC<EditRoleDialogProps> = ({ open, role, onClose, onSave, isSubmitting, mode }) => {
  const [name, setName] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [modulePermissions, setModulePermissions] = useState<ModulePermission[]>([]);
  const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set());

  const isEdit = useMemo(() => mode === 'edit', [mode]);

  useEffect(() => {
    if (role && isEdit) {
      setName(role.name);
      setIsActive(role.is_active);
      setModulePermissions(role.module);
      // Auto-expand modules with permissions
      const expanded = new Set<string>();
      role.module.forEach((m) => expanded.add(m.module_name));
      setExpandedModules(expanded);
    } else if (mode === 'create') {
      setName('');
      setIsActive(true);
      setModulePermissions([]);
      setExpandedModules(new Set());
    }
  }, [role, mode, open]);

  const handlePermissionChange = (moduleName: ModuleName, permission: Permission, checked: boolean) => {
    setModulePermissions((prev) => {
      const existing = prev.find((m) => m.module_name === moduleName);

      if (checked) {
        // Add permission
        if (existing) {
          return prev.map((m) =>
            m.module_name === moduleName ? { ...m, permissions: [...m.permissions, permission] } : m,
          );
        } else {
          return [...prev, { module_name: moduleName, permissions: [permission] }];
        }
      } else {
        // Remove permission
        if (existing) {
          const newPermissions = existing.permissions.filter((p) => p !== permission);
          if (newPermissions.length === 0) {
            return prev.filter((m) => m.module_name !== moduleName);
          }
          return prev.map((m) => (m.module_name === moduleName ? { ...m, permissions: newPermissions } : m));
        }
        return prev;
      }
    });
  };

  const isPermissionChecked = (moduleName: ModuleName, permission: Permission): boolean => {
    const module = modulePermissions.find((m) => m.module_name === moduleName);
    return module ? module.permissions.includes(permission) : false;
  };

  const handleSelectAll = (moduleName: ModuleName, checked: boolean) => {
    const allowedPerms = getModulePermissions(moduleName);
    if (checked) {
      // Add all allowed permissions for this module
      setModulePermissions((prev) => {
        const existing = prev.find((m) => m.module_name === moduleName);
        if (existing) {
          return prev.map((m) => (m.module_name === moduleName ? { ...m, permissions: [...allowedPerms] } : m));
        } else {
          return [...prev, { module_name: moduleName, permissions: [...allowedPerms] }];
        }
      });
    } else {
      // Remove all permissions for this module
      setModulePermissions((prev) => prev.filter((m) => m.module_name !== moduleName));
    }
  };

  const isModuleFullySelected = (moduleName: string): boolean => {
    const module = modulePermissions.find((m) => m.module_name === moduleName);
    const allowedPerms = getModulePermissions(moduleName as any);
    return module ? module.permissions.length === allowedPerms.length : false;
  };

  const getModulePermissionCount = (moduleName: string): number => {
    const module = modulePermissions.find((m) => m.module_name === moduleName);
    return module ? module.permissions.length : 0;
  };

  const toggleModuleExpansion = (moduleName: string) => {
    setExpandedModules((prev) => {
      const next = new Set(prev);
      if (next.has(moduleName)) {
        next.delete(moduleName);
      } else {
        next.add(moduleName);
      }
      return next;
    });
  };

  const handleSave = async () => {
    // Sanitize permissions: only include allowed permissions per module
    const sanitizedModules = modulePermissions
      .map((m: ModulePermission) => {
        const allowedPerms = new Set(getModulePermissions(m.module_name));
        const validPermissions = m.permissions.filter((p) => allowedPerms.has(p as Permission));
        return { ...m, permissions: validPermissions };
      })
      .filter((m) => m.permissions.length > 0); // Remove modules with no permissions

    const data: CreateRoleRequest | UpdateRoleRequest = {
      name,
      is_active: isActive,
      module: sanitizedModules,
    };

    await onSave(data);
    onClose();
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h4" component="span" sx={{ fontWeight: 600 }}>
          {!isEdit ? 'Create New Role' : 'Edit Role'}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
            <FormField
              label="Role Name"
              tooltip="Unique name for this role (e.g., Monetization, Developer, QA)"
              required
            >
              <TextField
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
                placeholder="Enter role name"
                variant="outlined"
              />
            </FormField>

            <FormField label="Status" tooltip="Activate or deactivate this role">
              <FormControlLabel
                control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} color="success" />}
                label={''}
              />
            </FormField>
          </Box>

          <Divider sx={{ my: 1 }} />

          <Box>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Module Permissions
              </Typography>
              <Chip
                label={`${modulePermissions.length} module${modulePermissions.length !== 1 ? 's' : ''} configured`}
                size="small"
                color="primary"
                variant="outlined"
              />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Configure access permissions for each module. Click to expand and select permissions.
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {AVAILABLE_MODULES.map((moduleName) => {
                const permissionCount = getModulePermissionCount(moduleName);
                const isExpanded = expandedModules.has(moduleName);
                const hasPermissions = permissionCount > 0;
                const allowedPermissions = getModulePermissions(moduleName);

                return (
                  <Accordion
                    key={moduleName}
                    expanded={isExpanded}
                    onChange={() => toggleModuleExpansion(moduleName)}
                    sx={{
                      overflow: 'hidden',
                      border: 1,
                      borderBottom: (theme) =>
                        `1px solid ${hasPermissions ? theme.palette.primary.main : theme.palette.divider} !important`,
                      borderRadius: 1,
                      backgroundColor: 'background.paper',
                      borderColor: hasPermissions ? 'primary.main' : 'divider',
                    }}
                  >
                    <AccordionSummary
                      sx={{
                        py: 0.5,
                        '& .MuiAccordionSummary-content': {
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          my: 0,
                        },
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: hasPermissions ? 600 : 500,
                          textTransform: 'capitalize',
                        }}
                      >
                        {moduleName}
                      </Typography>
                      <Box
                        sx={{
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        {permissionCount > 0 && (
                          <StatusChip
                            label={`${permissionCount} / ${allowedPermissions.length} permissions`}
                            size="small"
                            status="success"
                            useBoxStyle
                            sx={{ mr: 2 }}
                          />
                        )}
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                          }}
                        >
                          <FormControlLabel
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                            control={
                              <Checkbox
                                checked={isModuleFullySelected(moduleName)}
                                indeterminate={permissionCount > 0 && !isModuleFullySelected(moduleName)}
                                onChange={(e) => {
                                  handleSelectAll(moduleName, e.target.checked);
                                }}
                              />
                            }
                            label={<Typography variant="body2">Select All</Typography>}
                            sx={{ m: 0 }}
                          />
                        </Box>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ '&.MuiAccordionDetails-root': { borderTop: 'none' } }}>
                      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, pl: 1 }}>
                        {allowedPermissions.map((permission) => (
                          <FormControlLabel
                            key={`${moduleName}-${permission}`}
                            control={
                              <Checkbox
                                checked={isPermissionChecked(moduleName, permission)}
                                onChange={(e) => handlePermissionChange(moduleName, permission, e.target.checked)}
                                size="small"
                              />
                            }
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2">{permission}</Typography>
                              </Box>
                            }
                          />
                        ))}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isSubmitting || !name}
          startIcon={isSubmitting && <CircularProgress size={16} />}
        >
          {isSubmitting ? 'Saving...' : !isEdit ? 'Create' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
